import { useEffect, useMemo, useState } from 'react';

import { PartialReceiver, SelectedReceiver, toSelectedReceiver } from '../components/transaction/SelectedReceiver';
import { useAccountNumber } from '../fetchHooks/useAccount';
import useOcr from '../fetchHooks/useOcr';
import { useSingleReceiver } from '../fetchHooks/useReceivers';
import type { AccountNumber, TransactionActor } from '../generated-types';
import isDomesticAccountNumber from '../utils/boolean/isDomesticAccountNumber';

export const useActorReceiver = (
    toAccount: TransactionActor | undefined,
    foreignReceiver = false,
): SelectedReceiver | undefined => {
    const [receiver, setReceiver] = useState<SelectedReceiver>();

    const { data: account, isFetched } = useAccountNumber(toAccount?.accountNumber);
    const needReceivers = isFetched && !account?.alias;
    const receiverState = useSingleReceiver(toAccount?.accountNumber, needReceivers);
    const needOcrData =
        isFetched && !account?.alias && !foreignReceiver && isDomesticAccountNumber(toAccount?.accountNumber);
    const { data: ocrInfo } = useOcr(toAccount?.accountNumber, needOcrData);

    useEffect(() => {
        if (toAccount && account?.alias) {
            setReceiver({ accountNumber: account.accountNumber, accountName: account.alias });
        } else if (toAccount?.accountNumber) {
            const newReceiver: PartialReceiver = receiverState.data ?? {
                accountNumber: toAccount.accountNumber,
                name: toAccount.name,
            };
            setReceiver(toSelectedReceiver(newReceiver, ocrInfo));
        }
    }, [account?.accountNumber, account?.alias, receiverState.data, ocrInfo, toAccount]);
    return useMemo(() => {
        if (receiver) return receiver;
        if (toAccount?.accountNumber) return { accountNumber: toAccount?.accountNumber, accountName: toAccount.name };
        if (toAccount?.iban) return { accountNumber: toAccount?.iban, accountName: toAccount.name };
        return undefined;
    }, [receiver, toAccount?.accountNumber, toAccount?.iban, toAccount?.name]);
};

export const useReceiver = (
    toAccount: AccountNumber | undefined,
    foreignReceiver = false,
): SelectedReceiver | undefined => {
    const actor = useMemo(
        () => (foreignReceiver ? undefined : { accountNumber: toAccount }),
        [foreignReceiver, toAccount],
    );
    return useActorReceiver(actor, foreignReceiver);
};

const useSetReceiver = (
    toAccount: AccountNumber | undefined,
    handleSetReceiver: (receiver: SelectedReceiver) => void,
    foreignReceiver = false,
): void => {
    const receiver = useReceiver(toAccount, foreignReceiver);

    useEffect(() => {
        if (receiver) {
            handleSetReceiver(receiver);
        }
    }, [handleSetReceiver, receiver]);
};

export default useSetReceiver;
